import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CheckCircle, Shield, MapPin, Users, ChevronRight } from 'lucide-react'

export default function Onboarding() {
  const navigate = useNavigate()
  const [currentSlide, setCurrentSlide] = useState(0)

  const slides = [
    {
      title: "Welcome to on record",
      subtitle: "Capture Authenticity, Share Reality",
      description: "Join the community where every moment is verified, every story is real, and trust is built through transparency.",
      icon: CheckCircle,
      color: "from-orange-500 to-red-500",
      iconColor: "text-orange-500"
    },
    {
      title: "on record Codes",
      subtitle: "Every moment, uniquely verified",
      description: "Each photo and video gets a unique 12-digit on record code with GPS, timestamp, and cryptographic proof of authenticity.",
      icon: Shield,
      color: "from-blue-500 to-indigo-500",
      iconColor: "text-blue-500"
    },
    {
      title: "Location Verified",
      subtitle: "GPS-stamped authenticity",
      description: "Instant GPS verification ensures content is captured exactly where and when it claims to be. No fake locations, no manipulation.",
      icon: MapPin,
      color: "from-green-500 to-emerald-500",
      iconColor: "text-green-500"
    },
    {
      title: "Community Trust",
      subtitle: "Built on transparency",
      description: "See authenticity scores, community validation, and make informed decisions about the content you consume.",
      icon: Users,
      color: "from-purple-500 to-pink-500",
      iconColor: "text-purple-500"
    }
  ]

  const handleNext = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1)
    } else {
      completeOnboarding()
    }
  }

  const handleSkip = () => {
    completeOnboarding()
  }

  const completeOnboarding = () => {
    localStorage.setItem('onboarding_completed', 'true')
    navigate('/signup')
  }

  const currentSlideData = slides[currentSlide]
  const Icon = currentSlideData.icon

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex flex-col">
      {/* Skip Button */}
      <div className="p-6 flex justify-end">
        <button
          onClick={handleSkip}
          className="text-white/70 hover:text-white transition-colors text-sm font-medium"
        >
          Skip
        </button>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-6">
        <div className="max-w-2xl w-full text-center">
          {/* Icon */}
          <div className={`inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br ${currentSlideData.color} mb-8 animate-pulse`}>
            <Icon className="w-12 h-12 text-white" />
          </div>

          {/* Title */}
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {currentSlideData.title}
          </h1>

          {/* Subtitle */}
          <p className={`text-xl md:text-2xl font-semibold ${currentSlideData.iconColor} mb-6`}>
            {currentSlideData.subtitle}
          </p>

          {/* Description */}
          <p className="text-lg text-gray-300 leading-relaxed max-w-xl mx-auto">
            {currentSlideData.description}
          </p>
        </div>
      </div>

      {/* Navigation */}
      <div className="p-6 space-y-6">
        {/* Progress Dots */}
        <div className="flex justify-center gap-2">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === currentSlide
                  ? 'w-8 bg-onrecord-orange'
                  : 'w-2 bg-white/30 hover:bg-white/50'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        {/* Next Button */}
        <div className="max-w-md mx-auto">
          <button
            onClick={handleNext}
            className="w-full flex items-center justify-center gap-2 px-8 py-4 bg-onrecord-orange hover:bg-onrecord-orange-dark text-white rounded-xl font-semibold text-lg transition-colors shadow-lg shadow-onrecord-orange/30"
          >
            {currentSlide === slides.length - 1 ? 'Get Started' : 'Next'}
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  )
}
